import { z } from 'zod';
/**
 * Club Lite — PR 21 Slice 4 (ADR-044 / ADR-045).
 *
 * V1 design (NORTH_STAR §4):
 *   - Each user belongs to at most ONE club
 *   - 3-tier weekly chest: Bronze (daily 1K Heat Points) / Gold (3 Bronze days) / Fever (league top5 OR 3-friends-online ≥30min)
 *   - Heat Points formula: max(spins / 10, fever_contribution_pct * 5) per session, summed across games
 *   - Weekly reset 00:00 UTC Monday; chest entitlements are weekly, not lifetime
 *
 * Out of scope for V1 (NORTH_STAR §4.2): chat, member roster pagination, club wars,
 * non-owner roles, banner cosmetics, cross-club leaderboard.
 */
export const ClubChestTier = z.enum(['bronze', 'gold', 'fever']);
export const ClubInventoryItemKind = z.enum([
    'coins',
    'heat_boost',
    'fever_spark',
    'streak_shield',
    'happy_hour_token',
    'frame_shard',
    'title_grant',
]);
export const ClubChestContent = z.object({
    kind: ClubInventoryItemKind,
    amount: z.number().int().positive(),
    meta: z.record(z.unknown()).optional(),
});
export const ClubChestState = z.object({
    tier: ClubChestTier,
    unlocked: z.boolean(),
    claimed: z.boolean(),
    progress_pct: z.number().int().min(0).max(100),
    unlock_hint: z.string(),
    contents: z.array(ClubChestContent),
});
/** A real or synthetic club member as surfaced in Club page (top-N by last_active_at). */
export const ClubMember = z.object({
    user_id: z.string(),
    display_name: z.string(),
    avatar_url: z.string().nullable(),
    role: z.enum(['owner', 'member']),
    /** Derived from users.last_seen_at delta. ≤2min online / ≤15min idle / else offline. */
    online_status: z.enum(['online', 'idle', 'offline']),
    /** For NPCs: from synthetic profile (35% in-room rotating every 10 min). For real users: from rooms.userToRoom. */
    current_room_id: z.string().nullable(),
    current_game_id: z.string().nullable(),
});
export const Club = z.object({
    id: z.string(),
    slug: z.string(),
    display_name: z.string(),
    emoji: z.string(),
    owner_id: z.string(),
    invite_code: z.string(),
    member_count: z.number().int().nonnegative(),
    online_friend_count: z.number().int().nonnegative(),
    online_member_count: z.number().int().nonnegative(),
    weekly_heat_points: z.number().int().nonnegative(),
    weekly_heat_target: z.number().int().positive(),
    consecutive_bronze_days: z.number().int().nonnegative(),
    three_friends_online_minutes: z.number().int().nonnegative(),
    chests: z.array(ClubChestState),
    members: z.array(ClubMember),
    is_owner: z.boolean(),
    created_at: z.string(),
});
/** Slug rules: 4-24 chars, lowercase letters / digits / `_` / `-`. Used in URLs and invite copy. */
export const CLUB_SLUG_REGEX = /^[a-z0-9_-]{4,24}$/;
export const CreateClubRequest = z.object({
    slug: z.string().regex(CLUB_SLUG_REGEX),
    display_name: z.string().min(1).max(32),
    emoji: z.string().min(1).max(8).default('🌸'),
});
/**
 * Join request: exactly one of invite_code (private/private-share path) or club_id
 * (public discover-feed path). XOR enforced via .refine — backend resolves either to a
 * single club row, then runs the same eligibility checks (already_in_a_club, archived).
 */
export const JoinClubRequest = z
    .object({
    invite_code: z.string().length(8).optional(),
    club_id: z.string().optional(),
})
    .refine((d) => Boolean(d.invite_code) !== Boolean(d.club_id), {
    message: 'exactly one of invite_code or club_id must be provided',
});
/** Discover feed item — one card in the empty-state Discover section. */
export const ClubDiscoverItem = z.object({
    id: z.string(),
    slug: z.string(),
    display_name: z.string(),
    emoji: z.string(),
    member_count: z.number().int().nonnegative(),
    friend_count_in_club: z.number().int().nonnegative(),
    weekly_heat_points: z.number().int().nonnegative(),
    weekly_heat_target: z.number().int().positive(),
    created_at: z.string(),
});
export const ClubDiscoverResponse = z.object({
    items: z.array(ClubDiscoverItem),
    total: z.number().int().nonnegative(),
    has_more: z.boolean(),
});
/** A single weekly Heat-points contributor (top-N surfaced on Club page). */
export const ClubTopContributor = z.object({
    user_id: z.string(),
    display_name: z.string(),
    avatar_url: z.string().nullable(),
    total_heat: z.number().int().nonnegative(),
    is_self: z.boolean(),
});
export const ClubMeResponse = z.object({
    club: Club.nullable(),
    top_contributors_this_week: z.array(ClubTopContributor).default([]),
});
/** Activity feed item — Club page "刚刚发生了什么" surface. */
export const ClubActivityKind = z.enum([
    'in_room', // member is currently in a slot room
    'streak', // member is on an N-day streak
    'fever_hit', // member triggered a fever recently
    'big_win', // member won big in a spin recently
    'chest_claimed', // member claimed a chest tier
    'joined_club', // member joined the club recently
    'online_recent', // member came online recently
]);
export const ClubActivity = z.object({
    id: z.string(),
    user_id: z.string(),
    display_name: z.string(),
    avatar_url: z.string().nullable(),
    kind: ClubActivityKind,
    text: z.string(),
    emoji: z.string(),
    timestamp: z.string(),
});
export const ClubActivityResponse = z.object({
    items: z.array(ClubActivity),
});
export const ClubResponse = z.object({
    club: Club,
});
export const ClubChestClaimResponse = z.object({
    tier: ClubChestTier,
    contents: z.array(ClubChestContent),
    new_balance: z.number().int(),
    claimed_at: z.string(),
});
/**
 * Per-tier chest payouts (NORTH_STAR §4.3 — 70/20/10 resource/social/display split).
 * Hardcoded V1; future cohorts/live-ops can override via game_configs entry `club_chest_v1`.
 */
export const CLUB_CHEST_REWARDS = {
    bronze: [
        { kind: 'coins', amount: 50_000 },
        { kind: 'heat_boost', amount: 1 },
    ],
    gold: [
        { kind: 'coins', amount: 200_000 },
        { kind: 'fever_spark', amount: 1 },
        { kind: 'frame_shard', amount: 1 },
    ],
    fever: [
        { kind: 'coins', amount: 500_000 },
        { kind: 'title_grant', amount: 1, meta: { duration_days: 7 } },
        { kind: 'happy_hour_token', amount: 1 },
    ],
};
/** Daily Heat Points target for Bronze unlock (NORTH_STAR §4.4 — V1 hardcoded). */
export const CLUB_DAILY_HEAT_TARGET = 1000;
/** Bronze unlock days within a week required to unlock Gold (NORTH_STAR §4.3). */
export const CLUB_GOLD_BRONZE_DAYS_REQUIRED = 3;
/** Friends-online minutes required for Fever Chest co-presence trigger. */
export const CLUB_FEVER_FRIENDS_ONLINE_MINUTES = 30;
export const CLUB_FEVER_FRIENDS_ONLINE_COUNT = 3;
/**
 * Heat Points formula (ADR-045 candidate).
 * Per-session contribution; summed across all games during the day.
 *
 * V1 tuning (2026-05-04, deliberately generous for launch demo):
 *   fromSpins = spins (1 point per spin)
 *   fromFever = floor(fever_contribution_pct * 200)   — top contributor gets 200; mid (50%) gets 100
 *
 * Calibration target: a typical 4-member room with 2-3 fevers/day and one engaged player
 * crosses the 1000 daily target → Bronze unlocks consistently for active clubs. Will retune
 * downward (probably ÷2 or ÷4) once real launch traffic produces a points distribution.
 *
 * Why generous-on-launch: an UNREACHABLE Bronze chest = onboarding broken (no chest claim
 * UX validation, no inventory items in user wallets, no demo-able flow). Better to over-pay
 * V1 → tune down once we see the curve, than starve users in week 1.
 */
export function computeHeatPoints(args) {
    const fromSpins = args.spins;
    const fromFever = Math.floor(args.fever_contribution_pct * 200);
    return Math.max(fromSpins, fromFever);
}
//# sourceMappingURL=index.js.map